import React, { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  Save, 
  Upload,
  X,
  Image as ImageIcon,
  FileUp
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import * as pdfjsLib from 'pdfjs-dist'
import Tesseract from 'tesseract.js'
// Configure worker URL for Vite bundler (ESM worker)
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

const AddAlert = () => {
  const navigate = useNavigate()
  const pdfInputRef = useRef(null)
  
  const [formData, setFormData] = useState({
    title: '',
    note: '',
    image: null,
    plan: '',
    discountPlan: '',
    createdDate: new Date().toISOString().split('T')[0]
  })
  const [loading, setLoading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [isFree, setIsFree] = useState(true)
  const [uploadProgress, setUploadProgress] = useState(0)
  const { addAlertBoth, uploadImage } = useFirebase()
  const [pdfFile, setPdfFile] = useState(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  const handleImportPdfClick = () => {
    if (pdfInputRef.current) pdfInputRef.current.click()
  }

  const extractPdfDetails = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      const page1 = await pdf.getPage(1)
      const viewport = page1.getViewport({ scale: 1.6 })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext('2d')
      await page1.render({ canvasContext: ctx, viewport }).promise
      const imageBlob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85))

      // Build title using bold + size heuristic near top
      let title = ''
      const tc = await page1.getTextContent()
      const items = (tc.items || []).filter(it => (it.str || '').trim())
      const styles = tc.styles || {}
      const topY = viewport.height * 0.65
      const buckets = new Map()
      items.forEach(it => {
        const t = it.transform || [1,0,0,1,0,0]
        const y = t[5] || 0
        const size = Math.max(Math.abs(t[0]||0), Math.abs(t[3]||0))
        const fontName = it.fontName
        const st = styles[fontName] || {}
        const isBold = /bold|black|heavy|700|800|900/i.test(`${st.fontWeight||''} ${st.fontStyle||''} ${fontName||''}`)
        const key = Math.round(y/4)*4
        const rec = buckets.get(key) || { y, parts: [] }
        rec.parts.push({ text: it.str, size, y, isBold })
        rec.y = Math.max(rec.y, y)
        buckets.set(key, rec)
      })
      const lines = Array.from(buckets.values()).map(l=>{
        const txt = l.parts.map(p=>p.text).join(' ').replace(/\s+/g,' ').trim()
        const avg = l.parts.reduce((a,b)=>a+(b.size||0),0)/(l.parts.length||1)
        const boldCount = l.parts.filter(p=>p.isBold).length
        const score = avg * (l.y >= topY ? 2 : 1) * (boldCount>0 ? 2 : 1)
        return { y: l.y, text: txt, avg, boldCount, score }
      }).filter(l=>l.text)
      lines.sort((a,b)=> b.score - a.score)
      const pick = lines[0]
      if (pick) title = pick.text.slice(0,150)

      // Description: first 2-3 pages text
      const pagesToRead = Math.min(3, pdf.numPages)
      let desc = ''
      for (let i=1;i<=pagesToRead;i++){
        const p = await pdf.getPage(i)
        const tci = await p.getTextContent()
        desc += ' ' + tci.items.map(it=>it.str||'').join(' ')
      }
      desc = desc.replace(/\s+/g,' ').trim()
      // Remove title text from start of desc if repeated
      if (title && desc.toLowerCase().startsWith(title.toLowerCase())) {
        desc = desc.slice(title.length).trim()
      }

      // OCR fallback if no text
      if ((!title || title.length<3) && (!desc || desc.length<20) && imageBlob){
        try{
          const url = URL.createObjectURL(imageBlob)
          const worker = await Tesseract.createWorker('eng')
          const { data } = await worker.recognize(url)
          await worker.terminate(); URL.revokeObjectURL(url)
          const otext = (data?.text||'').replace(/\s+/g,' ').trim()
          if (otext){
            const first = otext.split(/[\.!?\n]/).map(s=>s.trim()).filter(Boolean)
            title = title || first[0]?.slice(0,150) || ''
            desc = desc || otext
          }
        }catch{}
      }

      return { title, description: desc, imageBlob }
    } catch (err) {
      return { title: '', description: '', imageBlob: null }
    }
  }

  const handlePdfImportSelected = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') { toast.error('Please select a PDF'); e.target.value=''; return }
    setPdfFile(file)
    // Immediately set a filename-based title so the field is populated even if parsing fails
    const filenameTitle = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g,' ').trim()
    setFormData(prev => ({ ...prev, title: filenameTitle || prev.title }))
    try{
      setLoading(true)
      const { title, description, imageBlob } = await extractPdfDetails(file)
      const fallbackTitle = (title && title.trim()) || file.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g,' ').trim()
      const fallbackDesc = (description && description.trim()) || ''
      setFormData(prev=>({
        ...prev,
        title: fallbackTitle || prev.title,
        note: fallbackDesc || prev.note
      }))
      if (imageBlob){
        const imageFile = new File([imageBlob], `${(title||'note')}.jpg`, { type: 'image/jpeg' })
        setFormData(prev=>({ ...prev, image: imageFile }))
      }
      if (title || description || imageBlob) {
        toast.success('PDF data filled into form')
      } else {
        toast('No extractable text found in PDF. Title set from filename.', { icon: 'ℹ️' })
      }
    }catch(err){
      // Keep filename-based title even if extraction fails
      setFormData(prev=>({ ...prev, title: filenameTitle || prev.title }))
      toast.error('Failed to read PDF')
    }finally{
      setLoading(false)
      e.target.value=''
    }
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, image: file }))
        toast.success('Image uploaded successfully!')
      } else {
        toast.error('Please upload an image file')
      }
    }
  }, [])

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, image: file }))
        toast.success('Image uploaded successfully!')
      } else {
        toast.error('Please upload an image file')
      }
    }
  }

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title.trim() || !formData.note.trim()) {
      toast.error('Please fill in title and note')
      return
    }

    try {
      setLoading(true)
      console.log('🚀 Starting note creation process...')
      
      let imageUrl = ''
      let pdfUrl = ''

      // Upload image if provided
      if (formData.image) {
        try {
          console.log('📤 Uploading image to Firebase Storage...')
          setUploadProgress(25)
          
          imageUrl = await uploadImage(formData.image, 'alerts')
          setUploadProgress(75)
          
          console.log('✅ Image uploaded successfully:', imageUrl)
        } catch (uploadError) {
          console.error('❌ Image upload failed:', uploadError)
          toast.error('Image upload failed: ' + uploadError.message)
          setLoading(false)
          return
        }
      }
      
      // Upload PDF if provided
      if (pdfFile) {
        try{
          setUploadProgress(85)
          pdfUrl = await uploadImage(pdfFile, 'notes-pdf')
        }catch(pdfErr){
          console.error('PDF upload failed:', pdfErr)
          toast.error('PDF upload failed')
        }
      }

      setUploadProgress(90)
      
      // Prepare alert data for Firebase
      const alertData = {
        title: formData.title.trim(),
        message: formData.note.trim(),
        imageUrl: imageUrl || '',
        pdfUrl: pdfUrl || '',
        plan: formData.plan || null,
        discountPlan: formData.discountPlan || null,
        type: 'alert',
        status: 'draft',
        priority: 'medium',
        targetUsers: 'all',
        createdDate: formData.createdDate,
        createdBy: 'admin@nursingexam.com'
      }

      console.log('💾 Saving note to Firestore:', alertData)

      // Save to Firebase using addAlertBoth (RTDB + Firestore)
      await addAlertBoth(alertData)
      
      setUploadProgress(100)
      console.log('✅ Note created successfully!')
      toast.success('Note created successfully!')
      navigate('/alerts')
    } catch (error) {
      console.error('Error creating note:', error)
      toast.error('Failed to create note')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Simple Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/alerts')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Create Note</h1>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Note Details</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Import from PDF */}
            <div className="flex items-center justify-between">
              <button type="button" onClick={handleImportPdfClick} className="btn-secondary inline-flex items-center">
                <FileUp className="h-5 w-5 mr-2" /> Import from PDF
              </button>
              <input type="file" accept="application/pdf" ref={pdfInputRef} onChange={handlePdfImportSelected} className="hidden" />
            </div>
            {/* Image Upload - Drag & Drop */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                {formData.image ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <ImageIcon className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formData.image.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {(formData.image.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeImage}
                      className="p-1 hover:bg-red-100 rounded-full transition-colors"
                    >
                      <X className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="input w-full"
                placeholder="Enter note title..."
                required
              />
            </div>

            {/* Note (Message) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note *
              </label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={4}
                className="input w-full"
                placeholder="Enter your note..."
                required
              />
            </div>

            {/* Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isFree}
                    onChange={(e) => {
                      setIsFree(e.target.checked)
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, plan: '' }))
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Free</span>
                </label>
                {!isFree && (
                  <input
                    type="text"
                    name="plan"
                    value={formData.plan}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="Enter plan amount (e.g., 50 rs, 100 rs)..."
                  />
                )}
              </div>
            </div>

            {/* Discount Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Plan
              </label>
              <input
                type="text"
                name="discountPlan"
                value={formData.discountPlan}
                onChange={handleChange}
                className="input w-full"
                placeholder="Enter discount plan (e.g., 20% off, 30 rs discount)..."
              />
            </div>

            {/* Created Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date
              </label>
              <input
                type="date"
                name="createdDate"
                value={formData.createdDate}
                onChange={handleChange}
                className="input w-full"
              />
            </div>

            {/* Actions */}
            {/* Progress Bar */}
            {loading && uploadProgress > 0 && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Creating alert...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/alerts')}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Creating Note...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Save Note</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddAlert
